import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { kBiMaxTotalsParts } from '@euler/model/bi/blend';
import { BiDatasetCatalog } from '@euler/model/bi/catalog';
import { BiUploadedDatasetView } from '@euler/model/bi/dataset';
import { BiVizType } from '@euler/model/bi/viz';
import { Plus } from 'lucide-react-native';
import { memo } from 'react';
import { View } from 'react-native';
import { BiButton } from '../../../components/ui/BiButton';
import {
  BiEditorState,
  biEditorQueryNew,
  biEditorWithTotalsParts,
} from '../functions/biEditorState';
import { TotalsPart } from './TotalsPart';

/**
 * **合计其他数据…**: a KPI group's numbers from more datasets, each its
 * own query (measures, time range, filters) shown beside the first one's.
 */
export const TotalsSection = memo(
  ({
    state,
    builtin,
    uploaded,
    onChange,
    onRetryUploads,
  }: {
    state: BiEditorState;
    builtin: BiDatasetCatalog[];
    uploaded: BiUploadedDatasetView[] | null;
    onChange: (next: BiEditorState) => void;
    onRetryUploads: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    if (state.vizType !== BiVizType.KpiGroup || state.blend) return null;
    const parts = state.totalsParts;
    const withParts = (next: typeof parts, removed?: number) =>
      onChange(biEditorWithTotalsParts(state, next, removed));
    return (
      <View style={{ gap: rpx(12) }}>
        {parts.map((part, index) => (
          <TotalsPart
            key={index}
            index={index}
            part={part}
            builtin={builtin}
            uploaded={uploaded}
            onChange={next =>
              withParts(parts.map((entry, at) => (at === index ? next : entry)))
            }
            onRemove={() =>
              withParts(
                parts.filter((_, at) => at !== index),
                index,
              )
            }
            onRetryUploads={onRetryUploads}
          />
        ))}
        <View style={{ gap: rpx(6), alignItems: 'flex-start' }}>
          <BiButton
            icon={Plus}
            title="合计其他数据…"
            disabled={parts.length + 1 >= kBiMaxTotalsParts}
            onPress={() => withParts([...parts, biEditorQueryNew()])}
          />
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            从另一份数据再取几个数，和上面的并排显示，例如接车台次和检出异常数
          </Typography.Text>
        </View>
      </View>
    );
  },
);
